import { useEffect, useState } from 'react';
import {
  App as AntdApp,
  Button,
  Form as AntForm,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormActions, FormField } from '../../shared/form';
import {
  MemberEmailBlacklistRuleSchema,
  type MemberEmailBlacklistRuleDTO,
} from '../../schemas/memberEmailBlacklist';
import { authFetch } from '../../utils/api';
import { tokenManager } from '../../utils/tokenManager';
import { useBrandOptions } from '../../hooks/useBrandOptions';

type Rule = {
  id: number;
  scope: 'brand' | 'global';
  brand: string | null;
  pattern: string;
  description: string | null;
  isEnabled: boolean;
  updatedAt: string;
};

const defaults: MemberEmailBlacklistRuleDTO = {
  scope: 'brand',
  targetBrand: '',
  pattern: '',
  description: '',
  enabled: true,
};

export function MemberEmailBlacklistPage() {
  const { message, modal } = AntdApp.useApp();
  const [rows, setRows] = useState<Rule[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Rule | null>(null);
  const brands = useBrandOptions();
  const allBrand = tokenManager.isAllBrandAdmin();
  const allowedBrands = tokenManager.getAllowedBrands();
  const [selectedBrand, setSelectedBrand] = useState(
    () => allowedBrands[0] ?? ''
  );
  const form = useForm<MemberEmailBlacklistRuleDTO>({
    resolver: zodResolver(MemberEmailBlacklistRuleSchema),
    mode: 'onBlur',
    defaultValues: defaults,
  });

  const load = async () => {
    if (!selectedBrand) return;
    const response = await authFetch(
      `/api/admin/member-email-blacklist-rules/?brand=${encodeURIComponent(selectedBrand)}`
    );
    const result = await response.json();
    if (result.code === 0) setRows(result.data);
  };

  useEffect(() => {
    void load();
  }, [selectedBrand]);

  const close = () => {
    setOpen(false);
    setEditing(null);
    form.reset(defaults);
  };

  const submit = async (values: MemberEmailBlacklistRuleDTO) => {
    const path = editing
      ? `/api/admin/member-email-blacklist-rules/${editing.id}`
      : '/api/admin/member-email-blacklist-rules/';
    const response = await authFetch(path, {
      method: editing ? 'PUT' : 'POST',
      body: JSON.stringify(values),
      headers: values.targetBrand
        ? { 'X-Target-Brand': values.targetBrand }
        : undefined,
    });
    const result = await response.json();
    if (result.code !== 0) {
      message.error(result.msg || '儲存失敗');
      return;
    }
    message.success('已儲存');
    close();
    await load();
  };

  const toggle = async (row: Rule) => {
    try {
      const response = await authFetch(
        `/api/admin/member-email-blacklist-rules/${row.id}/toggle`,
        {
          method: 'PATCH',
          body: JSON.stringify({ enabled: !row.isEnabled }),
          headers: row.brand
            ? { 'X-Target-Brand': row.brand }
            : undefined,
        }
      );
      const result = await response.json();
      if (result.code !== 0) {
        message.error(result.msg || '更新失敗');
        return;
      }
      await load();
    } catch {
      message.error('更新失敗，請稍後再試');
    }
  };

  const remove = async (row: Rule) => {
    try {
      const response = await authFetch(
        `/api/admin/member-email-blacklist-rules/${row.id}`,
        {
          method: 'DELETE',
          headers: row.brand
            ? { 'X-Target-Brand': row.brand }
            : undefined,
        }
      );
      const result = await response.json();
      if (result.code !== 0) {
        message.error(result.msg || '刪除失敗');
        return;
      }
      message.success('已刪除');
      await load();
    } catch {
      message.error('刪除失敗，請稍後再試');
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Select
          aria-label="目標品牌"
          value={selectedBrand}
          onChange={setSelectedBrand}
          options={brands
            .filter(brand => allowedBrands.includes(brand.value))
            .map(brand => ({ label: brand.label, value: brand.value }))}
          style={{ width: 180 }}
        />
        <Button
          type="primary"
          onClick={() => {
            form.reset({ ...defaults, targetBrand: selectedBrand });
            setOpen(true);
          }}
        >
          新增黑名單規則
        </Button>
      </Space>
      <Table
        rowKey="id"
        dataSource={rows}
        columns={[
          {
            title: '範圍',
            render: (_, row: Rule) => (
              <Tag color={row.scope === 'global' ? 'red' : 'blue'}>
                {row.scope === 'global' ? '全部品牌' : row.brand}
              </Tag>
            ),
          },
          { title: '關鍵字', dataIndex: 'pattern' },
          { title: '備註', dataIndex: 'description' },
          {
            title: '狀態',
            render: (_, row: Rule) => (
              <Switch
                checked={row.isEnabled}
                onChange={() =>
                  modal.confirm({
                    title: row.isEnabled
                      ? '停用黑名單規則'
                      : '啟用黑名單規則',
                    content: `確定要${row.isEnabled ? '停用' : '啟用'}此規則？`,
                    onOk: () => toggle(row),
                  })
                }
              />
            ),
          },
          {
            title: '操作',
            render: (_, row: Rule) => (
              <Space>
                <Button
                  onClick={() => {
                    setEditing(row);
                    form.reset({
                      scope: row.scope,
                      targetBrand: row.brand ?? '',
                      pattern: row.pattern,
                      description: row.description ?? '',
                      enabled: row.isEnabled,
                    });
                    setOpen(true);
                  }}
                >
                  編輯
                </Button>
                <Button
                  danger
                  onClick={() =>
                    modal.confirm({
                      title: '刪除黑名單規則',
                      content: '確定要刪除此規則？',
                      onOk: () => remove(row),
                    })
                  }
                >
                  刪除
                </Button>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={open}
        title={editing ? '編輯 Email 黑名單' : '新增 Email 黑名單'}
        footer={null}
        onCancel={close}
        destroyOnHidden
      >
        <FormProvider {...form}>
          <AntForm
            layout="vertical"
            onFinish={form.handleSubmit(submit)}
          >
            <FormField<MemberEmailBlacklistRuleDTO>
              name="scope"
              label="範圍"
              required
              control={
                <Select
                  disabled={!!editing}
                  options={[
                    { label: '單一品牌', value: 'brand' },
                    ...(allBrand
                      ? [{ label: '全部品牌（僅超管）', value: 'global' }]
                      : []),
                  ]}
                />
              }
            />
            {form.watch('scope') === 'brand' && (
              <FormField<MemberEmailBlacklistRuleDTO>
                name="targetBrand"
                label="目標品牌"
                required
                control={
                  <Select
                    disabled={!!editing}
                    options={brands
                      .filter(brand => allowedBrands.includes(brand.value))
                      .map(brand => ({
                        label: brand.label,
                        value: brand.value,
                      }))}
                  />
                }
              />
            )}
            <FormField<MemberEmailBlacklistRuleDTO>
              name="pattern"
              label="Email 關鍵字"
              required
              help="純文字 contains，比對時忽略大小寫；不可使用空白或正規表示式。"
              control={<Input />}
            />
            <FormField<MemberEmailBlacklistRuleDTO>
              name="description"
              label="備註"
              control={<Input.TextArea rows={3} />}
            />
            <FormField<MemberEmailBlacklistRuleDTO>
              name="enabled"
              label="立即啟用"
              valuePropName="checked"
              control={<Switch />}
            />
            <FormActions
              onCancel={close}
              submitting={form.formState.isSubmitting}
              dirty={form.formState.isDirty}
              submitLabel={editing ? '儲存變更' : '建立規則'}
            />
          </AntForm>
        </FormProvider>
      </Modal>
    </>
  );
}
